import shipmentsFixture from '../../fixtures/shipments.json';
import sitesFixture from '../../fixtures/sites.json';
import {
  canCancelShipment,
  CARGONIZER_RETURN_CARRIERS,
  packageMissingMeasurements,
  returnIsCollected,
  type CargonizerCustomer,
  type Shipment,
  type ShipmentAddress,
  type ShipmentCreateInput,
  type ShipmentCreateReturnInput,
  type ShipmentCreateViaCargonizerInput,
  type ShipmentEndpoint,
  type ShipmentEvidence,
  type ShipmentList,
  type ShipmentPatch,
  type TrackingRefreshResult,
} from '../domain/shipment';
import { shipmentSchema, siteSchema } from '../domain/validation';
import { ConflictError, NotFoundError, ShipmentBookingError, ValidationError } from './errors';
import type { ShipmentsApi } from './shipments-api';
import { withoutUndefined } from './patch';

let mockShipmentCounter = 0;
function mockId(prefix: string): string {
  mockShipmentCounter += 1;
  return `${prefix}-${Date.now()}-${mockShipmentCounter}`;
}

/**
 * Deterministic mock for the Cargonizer-backed pallet tracking feature
 * (remora SPEC.md §9a/§9b, tidaltech/koi#7882). Fixture data is the real
 * Salmar Trælvik shipment (consignment 40170727320828768412), included to
 * give a concrete feel for the experience — updates here don't persist past
 * a page reload, unlike the live adapter.
 */

const wait = async (milliseconds = 80): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const clone = <T>(value: T): T => structuredClone(value);

// Real entries from Cargonizer's own address book ("kunder"), so the mock
// picker in dev/tests matches what the live adapter actually returns.
const MOCK_CARGONIZER_CUSTOMERS: CargonizerCustomer[] = [
  {
    id: '40208822',
    name: 'Salmar Trælvik',
    address: { line1: 'Ørnfjordveien 65', postcode: '9388', city: 'Fjordgard' },
    contactPerson: 'Anders Meyer',
    phone: '97 11 40 50',
  },
  {
    id: '40127996',
    name: 'Multi Marine',
    address: { line1: 'Baseveien 15', postcode: '6530', city: 'Averøy' },
    contactPerson: 'Multi Marine',
    email: 'heidisorli@tidalx.ai',
    phone: '95797677',
  },
];

// Mirrors leatherback's --shipment_return_destination_sites default.
const RETURN_DESTINATION_SITE_NAMES = ['Multimarine', 'KSU-WH', 'KSU-WS'];

const mockSites = (sitesFixture as unknown[]).map((raw) => siteSchema.parse(raw));

function completeAddress(address: ShipmentAddress | undefined): boolean {
  return Boolean(address?.line1.trim() && address.postcode.trim() && address.city.trim());
}

// A minimal valid PDF, standing in for a Cargonizer label.
const MOCK_LABEL_PDF = '%PDF-1.4\n1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj\n2 0 obj<</Type/Pages/Kids[]/Count 0>>endobj\ntrailer<</Root 1 0 R>>\n%%EOF\n';

export class MockShipmentsApi implements ShipmentsApi {
  private shipments: Shipment[] = (shipmentsFixture as unknown[]).map((raw) =>
    shipmentSchema.parse(raw),
  );
  // Evidence by shipment id, keyed by the same dedupe idea as leatherback.
  private evidence = new Map<string, Map<string, ShipmentEvidence>>();

  async listShipments(): Promise<ShipmentList> {
    await wait();
    return { shipments: clone(this.shipments), skipped: [] };
  }

  async getShipment(id: string): Promise<Shipment> {
    await wait();
    const shipment = this.shipments.find((candidate) => candidate.id === id);
    if (!shipment) throw new NotFoundError(`Shipment ${id} not found`);
    return clone(shipment);
  }

  async updateShipment(patch: ShipmentPatch): Promise<Shipment> {
    await wait(120);
    const index = this.shipments.findIndex((shipment) => shipment.id === patch.id);
    if (index < 0) throw new NotFoundError(`Shipment ${patch.id} was not found`);
    const existing = this.shipments[index];
    if (!existing) throw new NotFoundError(`Shipment ${patch.id} was not found`);
    if (patch.version !== existing.version) {
      throw new ConflictError(
        `Shipment ${patch.id} changed after it was loaded; no changes committed`,
        existing.version,
      );
    }

    const candidate = {
      ...existing,
      ...withoutUndefined(patch),
      id: existing.id,
      version: existing.version + 1,
      lastModified: new Date().toISOString(),
      lastModifiedBy: 'mock.current-user@example.test',
    };
    const parsed = shipmentSchema.parse(candidate);
    this.shipments[index] = parsed;
    return clone(parsed);
  }

  async createShipment(input: ShipmentCreateInput): Promise<Shipment> {
    await wait(120);
    const shipment = shipmentSchema.parse({
      ...input,
      id: mockId('shipment'),
      shippedAt: new Date().toISOString(),
      version: 0,
      lastModified: new Date().toISOString(),
      lastModifiedBy: 'mock.current-user@example.test',
      requestedBy: 'mock.current-user@example.test',
      unassignedSerials: [],
      pallets: input.pallets.map((pallet) => ({ ...pallet, serials: [], consumables: [] })),
    });
    this.shipments.push(shipment);
    return clone(shipment);
  }

  async createShipmentViaCargonizer(input: ShipmentCreateViaCargonizerInput): Promise<Shipment> {
    if (!input.senderReference.trim()) {
      throw new ValidationError(
        'sender_reference must be set: the project number to rebill the freight to, or "Intern frakt"',
      );
    }
    await wait(200);
    const consignmentNumber = mockId('mock-consignment');
    if (input.destinationSiteId) {
      const destinations = await this.listReturnDestinations();
      if (destinations.some((d) => d.siteId === input.destinationSiteId)) {
        throw new ShipmentBookingError(
          'destination is a Tidal location; book a customer return instead',
          'REVERSED_ENDPOINTS',
        );
      }
    }
    const shipment = shipmentSchema.parse({
      id: mockId('shipment'),
      direction: 'OUTBOUND',
      cargonizerConsignmentNumber: consignmentNumber,
      cargonizerConsignmentId: consignmentNumber,
      carrier: input.carrier,
      destinationCustomer: input.destinationCustomer,
      destinationAddress: input.destinationAddress,
      destinationSiteId: input.destinationSiteId,
      destinationContactName: input.destinationContactName,
      destinationContactPhone: input.destinationContactPhone,
      shippedAt: new Date().toISOString(),
      state: 'PENDING',
      reason: input.reason,
      senderReference: input.senderReference,
      version: 0,
      lastModified: new Date().toISOString(),
      lastModifiedBy: 'mock.current-user@example.test',
      requestedBy: 'mock.current-user@example.test',
      unassignedSerials: [],
      pallets: input.pallets.map((pallet, index) => ({
        ...pallet,
        id: mockId(`${consignmentNumber}-piece`),
        cargonizerPieceNumber: mockId(`${consignmentNumber}-piece-${index}`),
        serials: [],
        consumables: [],
      })),
    });
    this.shipments.push(shipment);
    return clone(shipment);
  }

  async createReturnShipmentViaCargonizer(input: ShipmentCreateReturnInput): Promise<Shipment> {
    if (!input.senderReference.trim()) {
      throw new ValidationError(
        'sender_reference must be set: the project number to rebill the freight to, or "Intern frakt"',
      );
    }
    await wait(200);
    const destinations = await this.listReturnDestinations();
    const destination = destinations.find((d) => d.siteId === input.destinationSiteId);
    if (input.originSiteId === input.destinationSiteId) {
      throw new ShipmentBookingError('origin and destination are the same site', 'REVERSED_ENDPOINTS');
    }
    if (destinations.some((d) => d.siteId === input.originSiteId)) {
      throw new ShipmentBookingError('origin is a Tidal location', 'REVERSED_ENDPOINTS');
    }
    if (!destination) {
      throw new ShipmentBookingError('destination is not an approved Tidal return location', 'REVERSED_ENDPOINTS');
    }
    if (
      returnIsCollected(input.carrier) &&
      (!input.origin.contactName?.trim() || !input.origin.contactPhone?.trim())
    ) {
      throw new ValidationError(
        `${input.carrier} collects the return, so the pickup needs a contact person and phone for the driver`,
      );
    }
    if (!completeAddress(input.origin.address)) {
      throw new ShipmentBookingError('origin address needs a street, postcode and city', 'INVALID_ADDRESS');
    }
    if (!(CARGONIZER_RETURN_CARRIERS as readonly string[]).includes(input.carrier)) {
      throw new ShipmentBookingError(`carrier ${input.carrier} has no return product`, 'UNSUPPORTED_PRODUCT');
    }
    const missing = packageMissingMeasurements(input.pallets);
    if (missing !== null) {
      throw new ShipmentBookingError(`package ${missing} needs its weight, length, width and height`, 'MISSING_MEASUREMENTS');
    }
    const originSite = mockSites.find((site) => site.id === input.originSiteId);
    const consignmentNumber = mockId('mock-return');
    const shipment = shipmentSchema.parse({
      id: mockId('shipment'),
      direction: 'RETURN',
      origin: {
        customerId: originSite?.customerId,
        siteId: input.originSiteId,
        name: input.origin.name,
        contactName: input.origin.contactName,
        contactPhone: input.origin.contactPhone,
        address: input.origin.address,
      },
      destinationCustomer: destination.name,
      destinationAddress: destination.address,
      destinationSiteId: destination.siteId,
      destinationCustomerId: destination.customerId,
      cargonizerConsignmentNumber: consignmentNumber,
      cargonizerConsignmentId: consignmentNumber,
      carrier: input.carrier,
      shippedAt: new Date().toISOString(),
      state: 'PENDING',
      reason: input.reason,
      senderReference: input.senderReference,
      version: 0,
      lastModified: new Date().toISOString(),
      lastModifiedBy: 'mock.current-user@example.test',
      requestedBy: 'mock.current-user@example.test',
      unassignedSerials: [],
      pallets: input.pallets.map((pallet, index) => ({
        ...pallet,
        id: mockId(`${consignmentNumber}-piece`),
        cargonizerPieceNumber: mockId(`${consignmentNumber}-piece-${index}`),
        serials: [],
        consumables: [],
      })),
    });
    this.shipments.push(shipment);
    return clone(shipment);
  }

  async listReturnDestinations(): Promise<ShipmentEndpoint[]> {
    await wait();
    // In the configured order, like leatherback: the first is the default.
    return RETURN_DESTINATION_SITE_NAMES.flatMap((name) => mockSites.filter((site) => site.name === name))
      .map((site) => ({
        customerId: site.customerId,
        siteId: site.id,
        name: site.displayName ?? site.name,
        address: site.address
          ? {
              line1: site.address.line1 ?? '',
              line2: site.address.line2,
              postcode: site.address.postcode ?? '',
              city: site.address.city ?? '',
            }
          : undefined,
      }));
  }

  async getShipmentLabel(id: string): Promise<Blob> {
    await wait();
    const shipment = this.shipments.find((candidate) => candidate.id === id);
    if (!shipment) throw new NotFoundError(`Shipment ${id} not found`);
    if (!shipment.cargonizerConsignmentId) {
      throw new NotFoundError('This shipment has no Cargonizer label.');
    }
    return new Blob([MOCK_LABEL_PDF], { type: 'application/pdf' });
  }

  async cancelShipment(id: string, version: number, note?: string): Promise<Shipment> {
    await wait(150);
    const shipment = this.shipments.find((candidate) => candidate.id === id);
    if (!shipment) throw new NotFoundError(`Shipment ${id} not found`);
    if (shipment.state === 'CANCELLED') return clone(shipment);
    if (!canCancelShipment(shipment)) {
      throw new ValidationError(
        'this shipment was already handed to the carrier, so it can’t be cancelled',
      );
    }
    if (version !== shipment.version) {
      throw new ConflictError(
        'Shipment changed after it was loaded; nothing was cancelled',
        shipment.version,
      );
    }
    shipment.state = 'CANCELLED';
    shipment.cancelNote = note?.trim() || undefined;
    shipment.version += 1;
    shipment.lastModified = new Date().toISOString();
    return clone(shipment);
  }

  async transferShipmentToCarrier(id: string, version: number): Promise<Shipment> {
    await wait(150);
    const shipment = this.shipments.find((candidate) => candidate.id === id);
    if (!shipment) throw new NotFoundError(`Shipment ${id} not found`);
    if (shipment.state === 'TRANSFERRED') return clone(shipment);
    if (version !== shipment.version) {
      throw new ConflictError('Shipment changed after it was loaded; nothing was transferred', shipment.version);
    }
    if (!shipment.cargonizerConsignmentId) {
      throw new ValidationError('Only shipments booked through koi can be transferred from koi.');
    }
    const recorded = this.evidence.get(id) ?? new Map<string, ShipmentEvidence>();
    this.evidence.set(id, recorded);
    if (!recorded.has('transferred')) {
      recorded.set('transferred', {
        id: mockId('evidence'),
        source: 'CARGONIZER',
        confirmation: 'CONFIRMED',
        kind: 'TRANSFERRED_TO_CARRIER',
        description: 'Transferred to the carrier from koi',
        eventTime: new Date().toISOString(),
        eventTimeApproximate: false,
      });
    }
    shipment.state = 'TRANSFERRED';
    shipment.version += 1;
    shipment.lastModified = new Date().toISOString();
    return clone(shipment);
  }

  async listShipmentEvidence(shipmentId: string): Promise<ShipmentEvidence[]> {
    await wait();
    const recorded = [...(this.evidence.get(shipmentId)?.values() ?? [])];
    return clone(recorded.sort((a, b) => (a.eventTime ?? '').localeCompare(b.eventTime ?? '')));
  }

  // Mirrors leatherback's RefreshShipmentTracking: the carrier hand-off, then
  // Bring events for Bring shipments. Replays record nothing new, and state
  // and delivery never move backwards.
  async refreshShipmentTracking(shipmentIds: string[]): Promise<TrackingRefreshResult> {
    await wait(150);
    const result: TrackingRefreshResult = {
      shipmentsChecked: shipmentIds.length,
      evidenceRecorded: 0,
      deliveriesRecorded: 0,
      bringEnabled: true,
      errors: [],
    };
    for (const id of shipmentIds) {
      const shipment = this.shipments.find((candidate) => candidate.id === id);
      if (!shipment) throw new NotFoundError(`Shipment ${id} not found`);
      if (!shipment.cargonizerConsignmentNumber) continue;
      const recorded = this.evidence.get(id) ?? new Map<string, ShipmentEvidence>();
      this.evidence.set(id, recorded);
      const record = (key: string, evidence: Omit<ShipmentEvidence, 'id'>) => {
        if (recorded.has(key)) return;
        recorded.set(key, { ...evidence, id: mockId('evidence') });
        result.evidenceRecorded += 1;
      };
      const shipped = new Date(shipment.shippedAt).getTime();
      const hoursAfterShipping = (hours: number) =>
        new Date(shipped + hours * 3_600_000).toISOString();
      record('transferred', {
        source: 'CARGONIZER',
        confirmation: 'CONFIRMED',
        kind: 'TRANSFERRED_TO_CARRIER',
        description: 'Transferred to the carrier in Cargonizer',
        eventTime: hoursAfterShipping(1),
        eventTimeApproximate: true,
      });
      let changed = false;
      if (shipment.state === 'PENDING') {
        shipment.state = 'TRANSFERRED';
        changed = true;
      }
      if (shipment.carrier.startsWith('Bring')) {
        record('in-transit', {
          source: 'BRING_TRACKING_API',
          confirmation: 'CONFIRMED',
          kind: 'IN_TRANSIT',
          description: 'The shipment is on its way',
          eventTime: hoursAfterShipping(20),
          eventTimeApproximate: false,
          location: 'Trondheim, NO',
        });
        const deliveredAt = hoursAfterShipping(44);
        record('delivered', {
          source: 'BRING_TRACKING_API',
          confirmation: 'CONFIRMED',
          kind: 'DELIVERED',
          description: 'The shipment has been delivered',
          eventTime: deliveredAt,
          eventTimeApproximate: false,
          location: `${shipment.destinationAddress.postcode} ${shipment.destinationAddress.city}, NO`,
        });
        if (!shipment.delivery) {
          shipment.delivery = {
            status: 'The shipment has been delivered',
            deliveredAt,
            source: 'BRING_TRACKING_API',
          };
          result.deliveriesRecorded += 1;
          changed = true;
        }
      }
      if (changed) {
        shipment.version += 1;
        shipment.lastModified = new Date().toISOString();
      }
    }
    return result;
  }

  async listCargonizerCustomers(): Promise<CargonizerCustomer[]> {
    await wait();
    return clone(MOCK_CARGONIZER_CUSTOMERS);
  }
}

export const shipmentsApi: ShipmentsApi = new MockShipmentsApi();
